import React, { useState, createContext, useContext } from "react";
import Swal from "sweetalert2";
import api from "../../Utils/api";

const apiPath = {
  fetchJobsByProject: "/jobnproject/getjobsbyproject",
  fetchJobsByProjectStandAlone: "/jobnproject/getstandalonejobs",
  addJob: "/jobnproject/addjob",
  updateJob: "/jobnproject/updatejobdetails",
  getAllSkills: "/jobnproject/getallskills",
  assignConsultantToJob: "/jobnproject/assignconsultant",
  assignSubVendorToJob: "/jobnproject/assignsubvendor",
  searchJob: "/jobnproject/searchjob",
  fetchJobDetails: "/jobnproject/getjobbyid",
  fetchJobsHavingTimesheet: "/jobnproject/getjobshavingtimesheet",
  fetchPublicJobs: "/jobnproject/getpublicjobs",
};

const nullFn = () => null;
const JobContext = createContext({
  loading: false,
  jobs: {},
  fetchJobsByProject: nullFn,
  addJob: nullFn,
  assignSubVendorToJob: nullFn,
  assignConsultantToJob: nullFn,
  allJobs: [],
  updateJob: nullFn,
  searchJob: nullFn,
  getAllSkills: nullFn,
  fetchJobDetails: nullFn,
  fetchJobsHavingTimesheet: nullFn,
  fetchPublicJobs: nullFn,
});

export const useJobProvider = () => useContext(JobContext);
const JobProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [skills, setSkills] = useState([]);

  const fetchPublicJobs = async (page, limit, serachText) => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchPublicJobs, {
        params:
          serachText.length > 0
            ? {
                page: page,
                size: limit,
                search: serachText,
              }
            : { page: page, size: limit },
      });

      if (res.status === 200 && res.data?.success) {
        setLoading(false)
        return res.data?.result;
      } else {
        throw new Error("Error occurred while fetching jobs");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsByProject = async (projectId) => {
    setLoading(true);
    try {
      let res;
      if (projectId) {
        res = await api.post(apiPath.fetchJobsByProject, {
          projectId,
          userId: localStorage.getItem("userId"),
        });
      } else {
        res = await api.post(apiPath.fetchJobsByProjectStandAlone, {
          projectId,
          userId: localStorage.getItem("userId"),
        });
      }

      if (res.status === 200 && res.data?.success) {
        setJobs({ ...jobs, [projectId]: res.data.result });
      } else {
        throw new Error("Error occurred while fetching jobs");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const addJob = async (job) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.addJob, job);
      if (res.status === 200 && res.data?.success) {
        setJobs({
          ...jobs,
          [job.project_id]: [...(jobs[job.project_id] ?? []), res.data.result],
        });

        setLoading(false);
        Swal.fire({
          title: "Success",
          text: "Job added successfully",
          icon: "success",
        });
        return res?.data?.result?.id;
      } else {
        throw new Error("Error occurred while creating new job");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        title: "Error",
        text: "Something went wrong",
        icon: "error",
      });
    }
  };

  const updateJob = async (job) => {
    setLoading(true);
    try {
      const res = await api.put(apiPath.updateJob + `/${job.id}`, job);
      if (res.status === 200 && res.data?.success) {
        setLoading(false);
        Swal.fire({
          title: "Success",
          text: "Job updated successfully",
          timer: 1500,
        });
        return res?.data?.result?.id;
      } else {
        throw new Error("Error occurred while updating the job");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    }
  };

  const searchJob = async (query, type) => {
    setLoading(true);
    try {
      return await api.get(`/jobnproject/searchjob`, { params: { query } });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const assignSubVendorToJob = async ({ job_id, vendor_id }) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.assignSubVendorToJob, {
        job_id,
        user_id: localStorage.getItem("userId"),
        vendor_id,
      });

      if (res.data?.success) {
        setLoading(false);
        Swal.fire({
          title: "Success",
          text: "Vendor assigned successfully",
          icon: "success",
        });
      } else {
        throw new Error("Error occurred while creating new job");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        title: "Error",
        text: "Something went wrong",
        icon: "error",
      });
    }
  };

  const assignConsultantToJob = async ({ job_id, consultant_id }) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.assignConsultantToJob, {
        job_id,
        consultant_id,
        user_id: localStorage.getItem("userId"),
      });

      if (res.status === 200 && res.data?.success) {
        setLoading(false);
        Swal.fire({
          title: "Success",
          text: "Consultant assigned successfully",
          icon: "success",
        });
      } else {
        throw new Error("Error occurred while creating new job");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        title: "Error",
        text: "Something went wrong",
        icon: "error",
      });
    }
  };

  const getAllSkills = async () => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.getAllSkills);
      if (res.status === 200 && res.data?.success) {
        setSkills(res.data.skill.rows);
      } else {
        throw new Error("Error occurred while fetching skills");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsHavingTimesheet = async () => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchJobsHavingTimesheet, {
        params: {
          userId: localStorage.getItem("userId"),
        },
      });

      if (res.status === 200 && res.data?.success) {
        setAllJobs(res.data.result);
      } else {
        throw new Error("Error occurred while fetching jobs");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };
  const fetchJobDetails = async (job_id) => {
    setLoading(true);
    try {
      const res = await api.get(`${apiPath.fetchJobDetails}/${job_id}`);

      if (res.status === 200 && res.data?.success) {
        setLoading(false);
        return res.data.result;
      } else {
        throw new Error("Error occurred while fetching job details");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      return null;
    }
  };

  const value = {
    loading,
    jobs,
    fetchJobsByProject,
    addJob,
    assignSubVendorToJob,
    assignConsultantToJob,
    allJobs,
    updateJob,
    searchJob,
    skills,
    getAllSkills,
    fetchJobDetails,
    fetchJobsHavingTimesheet,
    fetchPublicJobs
  };

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
};

export default JobProvider;
